import type { ApolloClient, NormalizedCacheObject } from "@apollo/client"
import { useSyncExternalStore } from "react"
import { getApolloClient } from "@pathwright/blocks-engine/src/graphql/client"
import { getGraphQLEndpoint } from "../pathwright/PathwrightClient"
import { usePathwrightContext } from "../pathwright/PathwrightContext"

type BlocksClient = ApolloClient<NormalizedCacheObject>

const clients = new Map<string, BlocksClient>()
let sessionToken: string | null | undefined
const readToken = () => localStorage.getItem("auth_token")

export const resetWebBlocksClients = () => {
  clients.forEach((client) => {
    client.stop()
    client.cache.restore({})
  })
  clients.clear()
}

export const getWebBlocksClient = ({
  schoolId,
  authToken = readToken()
}: {
  schoolId: number | string
  authToken?: string | null
}): BlocksClient => {
  if (sessionToken !== authToken) {
    resetWebBlocksClients()
    sessionToken = authToken
  }
  const endpoint = `${getGraphQLEndpoint()}blocks/graphql?school_id=${schoolId}`
  const existing = clients.get(endpoint)
  if (existing) return existing
  const client = getApolloClient({ endpoint, authToken })
  clients.set(endpoint, client)
  return client
}

const subscribe = (notify: () => void) => {
  const changed = () => {
    if (sessionToken !== readToken()) resetWebBlocksClients()
    notify()
  }
  window.addEventListener("auth_token", changed)
  window.addEventListener("storage", changed)
  return () => {
    window.removeEventListener("auth_token", changed)
    window.removeEventListener("storage", changed)
  }
}

export const useWebBlocksClient = (schoolId?: number | string) => {
  const context = usePathwrightContext()
  const authToken = useSyncExternalStore(subscribe, readToken, () => null)
  return getWebBlocksClient({
    schoolId: schoolId ?? context.school.id,
    authToken
  })
}
